import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Server, Send, Trash2 } from 'lucide-react';
import { API_BASE } from '../../../config/api';

const API = `${API_BASE}/api`;
const field = 'w-full h-10 bg-navigate-dark border border-zinc-700 rounded-lg text-sm text-white px-3';

const Field = ({ label, children }) => (
  <label className="block"><span className="text-xs text-zinc-400 mb-1 block">{label}</span>{children}</label>
);

export default function SmtpSettingsCard({ headers }) {
  const [s, setS] = useState(null);
  const [password, setPassword] = useState('');
  const [testTo, setTestTo] = useState('');
  const [busy, setBusy] = useState('');

  const load = () => axios.get(`${API}/admin/email/settings`, { headers }).then((r) => setS(r.data)).catch(() => toast.error('Failed to load email settings'));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy('save');
    try {
      await axios.put(`${API}/admin/email/settings`, { ...s, password: password || null }, { headers });
      toast.success('Email settings saved');
      setPassword('');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not save settings'); }
    finally { setBusy(''); }
  };
  const test = async () => {
    setBusy('test');
    try {
      await axios.post(`${API}/admin/email/test`, { to: testTo }, { headers });
      toast.success(`Test email sent to ${testTo}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Test failed'); }
    finally { setBusy(''); }
  };
  const clear = async () => {
    if (!window.confirm('Remove the saved email settings?')) return;
    await axios.delete(`${API}/admin/email/settings`, { headers });
    toast.success('Email settings removed');
    load();
  };

  if (!s) return <div className="h-64 rounded-xl bg-zinc-900 animate-pulse" />;
  const set = (k) => (e) => setS({ ...s, [k]: k === 'port' ? Number(e.target.value) : e.target.value });
  const status = s.source === 'platform' ? ['Using settings saved here', 'text-emerald-300 bg-emerald-500/10']
    : s.source === 'environment' ? ['Using server default settings — save your own below', 'text-amber-300 bg-amber-500/10']
      : ['Not set up', 'text-rose-300 bg-rose-500/10'];

  return (
    <form onSubmit={save} className="bg-navigate-surface border border-zinc-800 rounded-xl p-6 space-y-4" data-testid="smtp-settings-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2"><Server className="h-5 w-5 text-navigate-gold" />SMTP server</h2>
        <span className={`text-xs px-2.5 py-1 rounded-full ${status[1]}`} data-testid="smtp-status">{status[0]}</span>
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="sm:col-span-2"><Field label="SMTP host"><input value={s.host} onChange={set('host')} placeholder="smtp.example.com" className={field} data-testid="smtp-host-input" /></Field></div>
        <Field label="Port"><input type="number" value={s.port} onChange={set('port')} className={field} data-testid="smtp-port-input" /></Field>
        <Field label="Username"><input value={s.username} onChange={set('username')} autoComplete="off" className={field} data-testid="smtp-username-input" /></Field>
        <Field label={s.has_password ? 'Password (leave blank to keep)' : 'Password'}><input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" className={field} data-testid="smtp-password-input" /></Field>
        <Field label="Security">
          <select value={s.security} onChange={set('security')} className={field} data-testid="smtp-security-select">
            <option value="starttls">STARTTLS (usually port 587)</option>
            <option value="ssl">SSL/TLS (usually port 465)</option>
            <option value="none">None</option>
          </select>
        </Field>
        <Field label="From address"><input value={s.from_email} onChange={set('from_email')} placeholder="noreply@yourdomain.co.za" className={field} data-testid="smtp-from-email-input" /></Field>
        <Field label="From name"><input value={s.from_name} onChange={set('from_name')} className={field} data-testid="smtp-from-name-input" /></Field>
      </div>
      <div className="flex flex-wrap items-center gap-3 pt-2">
        <button type="submit" disabled={busy === 'save'} className="btn-gold px-5 py-2 text-sm disabled:opacity-50" data-testid="smtp-save-btn">{busy === 'save' ? 'Saving...' : 'Save settings'}</button>
        <input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="Send a test to..." className={`${field} max-w-xs`} data-testid="smtp-test-to-input" />
        <button type="button" onClick={test} disabled={!testTo || busy === 'test'} className="px-4 py-2 text-sm rounded-lg border border-zinc-700 text-zinc-200 hover:bg-zinc-800 flex items-center gap-1 disabled:opacity-50" data-testid="smtp-test-btn">
          <Send className="h-4 w-4" />{busy === 'test' ? 'Sending...' : 'Send test'}
        </button>
        {s.source === 'platform' && <button type="button" onClick={clear} className="ml-auto text-xs text-rose-300 hover:text-rose-200 flex items-center gap-1" data-testid="smtp-clear-btn"><Trash2 className="h-3 w-3" />Remove</button>}
      </div>
      {s.updated_at && <p className="text-xs text-zinc-500">Last saved {new Date(s.updated_at).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short' })} by {s.updated_by}</p>}
    </form>
  );
}
